/**
 * Environment Base URL business-rule validation.
 * `new URL()` is used only to parse components — success does not mean the URL is valid for Environments.
 */
/** Normalize for duplicate URL comparison within a project. */
export function normalizeEnvironmentUrlForComparison(url: string | null | undefined): string {
  const raw = (url ?? '').trim();
  if (!raw) return '';
  let normalized = raw;
  while (normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }
  return normalized.toLowerCase();
}

export function validateEnvironmentBaseUrl(url: string): string | null {
  const raw = (url ?? '').trim();
  if (!raw) return 'Environment URL is required';

  if (!/^https?:\/\//i.test(raw)) {
    return 'URL must start with http:// or https://';
  }

  if (raw.includes('\\')) {
    return 'URL path contains invalid characters';
  }

  const afterScheme = raw.replace(/^https?:\/\//i, '');
  const authority = afterScheme.split(/[/?#]/, 1)[0] ?? '';
  if (authority.includes('@')) {
    return 'Hostname contains invalid characters';
  }

  const { hostname, portError } = splitAuthority(authority);
  if (portError) return portError;

  const hostError = validateHostname(hostname);
  if (hostError) return hostError;

  const rest = afterScheme.slice(authority.length);
  const rawPathError = validateRawPathAndTail(rest);
  if (rawPathError) return rawPathError;

  // Reject fragments / bad % encoding before URL parse (parsers may throw first on %xc).
  if (raw.includes('#')) {
    return 'Invalid base url';
  }
  if (hasInvalidPercentEncoding(raw)) {
    return 'Invalid base url';
  }

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return 'Please enter a valid URL (e.g. https://api.example.com)';
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return 'URL must start with http:// or https://';
  }

  const pathError = validatePathname(parsed.pathname || '');
  if (pathError) return pathError;

  const queryError = validateQuery(parsed.search || '');
  if (queryError) return queryError;

  return null;
}

function splitAuthority(authority: string): { hostname: string; portError: string | null } {
  const colon = authority.lastIndexOf(':');
  if (colon <= 0) {
    return { hostname: authority, portError: null };
  }
  const maybePort = authority.slice(colon + 1);
  if (maybePort === '' || /[^0-9]/.test(maybePort) || !/^\d+$/.test(maybePort)) {
    // IPv6 would use brackets; Environment Base URL does not use IPv6 host:port here.
    if (!/^\d+$/.test(maybePort)) {
      return { hostname: authority.slice(0, colon), portError: 'Port must be numeric' };
    }
  }
  const n = Number(maybePort);
  if (n < 1 || n > 65535) {
    return { hostname: authority.slice(0, colon), portError: 'Port must be between 1 and 65535' };
  }
  return { hostname: authority.slice(0, colon), portError: null };
}

function validateHostname(host: string): string | null {
  if (!host) {
    return 'URL must contain a valid hostname';
  }
  const normalized = host.toLowerCase();
  if (normalized === 'localhost' || normalized === 'localhost.localdomain') {
    return null;
  }
  if (host.startsWith('.') || host.endsWith('.')) {
    return 'URL must contain a valid hostname';
  }
  if (host.includes('..')) {
    return 'Hostname cannot contain consecutive dots';
  }
  if (/[^a-z0-9.-]/i.test(host)) {
    return 'Hostname contains invalid characters';
  }

  const ipv4 = /^\d{1,3}(?:\.\d{1,3}){3}$/.test(host);
  if (ipv4) {
    const octets = host.split('.').map(Number);
    if (octets.some((n) => n > 255)) {
      return 'URL must contain a valid hostname';
    }
    return null;
  }

  // Allow single-label/internal hostnames (Docker/K8s/dev): sample, backend, api-server
  const labels = host.split('.');
  if (labels.some((label) => !label)) {
    return 'URL must contain a valid hostname';
  }
  for (const label of labels) {
    if (label.startsWith('-') || label.endsWith('-')) {
      return 'Hostname labels cannot start or end with a hyphen';
    }
    if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label)) {
      return 'Hostname contains invalid characters';
    }
  }
  // Multi-label FQDNs still require a letter/punycode TLD; single-label hosts skip this.
  if (labels.length > 1) {
    const tld = labels[labels.length - 1];
    const isPunycodeTld = /^xn--[a-z0-9-]{2,}$/i.test(tld);
    if (!isPunycodeTld && !/^[a-z]{2,}$/i.test(tld)) {
      return 'URL must contain a valid hostname';
    }
  }
  return null;
}

function validateRawPathAndTail(rest: string): string | null {
  if (!rest) return null;
  let pathEnd = rest.length;
  const queryAt = rest.indexOf('?');
  const hashAt = rest.indexOf('#');
  if (queryAt >= 0) pathEnd = Math.min(pathEnd, queryAt);
  if (hashAt >= 0) pathEnd = Math.min(pathEnd, hashAt);
  const pathPart = rest.slice(0, pathEnd);
  if (pathPart.startsWith('/')) {
    const pathError = validatePathname(pathPart);
    if (pathError) return pathError;
  } else if (pathPart.length > 0 && !pathPart.startsWith('?') && !pathPart.startsWith('#')) {
    return 'Please enter a valid URL (e.g. https://api.example.com)';
  }
  return null;
}

function validatePathname(pathname: string): string | null {
  if (!pathname || pathname === '/') {
    return null;
  }
  if (pathname.includes('//')) {
    return 'URL path contains repeated slashes';
  }
  if (!/^\/[A-Za-z0-9/_.~%-]*$/.test(pathname)) {
    return 'URL path contains invalid characters';
  }
  return null;
}

function validateQuery(search: string): string | null {
  if (!search) return null;
  const query = search.startsWith('?') ? search.slice(1) : search;
  if (query === '') return null;
  if (!/^[A-Za-z0-9._~=&%+\-\[\],:/]*$/.test(query)) {
    return 'URL path contains invalid characters';
  }
  return null;
}

/** Reject lone `%` or `%` not followed by two hex digits (e.g. `%xc`). */
function hasInvalidPercentEncoding(raw: string): boolean {
  for (let i = 0; i < raw.length; i += 1) {
    if (raw.charAt(i) !== '%') continue;
    const h1 = raw.charAt(i + 1);
    const h2 = raw.charAt(i + 2);
    if (!/^[0-9a-fA-F]$/.test(h1) || !/^[0-9a-fA-F]$/.test(h2)) {
      return true;
    }
    i += 2;
  }
  return false;
}
